import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { injectIntl } from 'react-intl'
import Table from '@vtex/styleguide/lib/Table'

import { intlShape } from '@/checkout-instore-legacy/i18n/intlShape'
import Loader from '@/checkout-instore-legacy/components/Commons/Loader'
import { formatTime } from '@/checkout-instore-legacy/utils/DateUtils'
import { getOrdersDate } from '@/checkout-instore-legacy/order/utils'

class OrderTable extends Component {
  constructor(props) {
    super(props)

    const { intl } = props

    const rowsPerPage = 10
    const currentItemTo = Math.min(rowsPerPage, props.ordersLength)
    const currentItemFrom = 1

    this.initialState = {
      rowsPerPage,
      currentPage: 1,
      currentItemFrom,
      currentItemTo,
      searchValue: '',
      emptyStateLabel: intl.formatMessage({
        id: 'orders.table.emptyOrders',
      }),
      filterStatements: [],
    }

    this.jsonSchema = this.getJsonSchemaData()

    this.state = this.initialState
  }

  getJsonSchemaData() {
    const { intl } = this.props

    return {
      properties: {
        creationDate: {
          type: 'string',
          title: intl.formatMessage({ id: 'orders.table.column.orderDate' }),
          width: 200,
          cellRenderer: this.creationDateRenderer,
        },
        orderId: {
          type: 'string',
          title: intl.formatMessage({ id: 'orders.table.column.orderId' }),
          width: 200,
        },
        clientName: {
          type: 'string',
          title: intl.formatMessage({ id: 'orders.client' }),
          width: 200,
        },
        totalValue: {
          type: 'number',
          title: intl.formatMessage({ id: 'orders.table.column.totalValue' }),
          width: 160,
          cellRenderer: this.totalValueRenderer,
        },
        vendorName: {
          type: 'string',
          title: intl.formatMessage({ id: 'orders.vendor' }),
          width: 200,
        },
      },
    }
  }

  totalValueRenderer = ({
    cellData: totalValue,
    rowData: { currencyCode },
  }) => {
    return this.props.intl.formatNumber(totalValue / 100, {
      style: 'currency',
      currency: currencyCode,
      currencyDisplay: 'symbol',
      minimumFractionDigits: 2,
    })
  }

  creationDateRenderer = ({ cellData: creationDate }) => {
    const { intl } = this.props
    const date = getOrdersDate(intl, creationDate)
    const time = formatTime(new Date(creationDate))
    return intl.formatMessage(
      {
        id: 'orders.table.orderDate',
      },
      { date, time }
    )
  }

  handleNextClick = () => {
    const currentPage = this.state.currentPage + 1
    this.props.handlePageLoad(
      null,
      this.props.endCursor,
      this.state.rowsPerPage,
      this.state.searchTerm
    )
    this.setState({ currentPage })
  }

  handlePrevClick = () => {
    const currentPage = this.state.currentPage - 1
    this.props.handlePageLoad(
      this.props.startCursor,
      null,
      this.state.rowsPerPage,
      this.state.searchTerm
    )
    this.setState({ currentPage })
  }

  handleInputSearchChange = (e) => {
    this.setState({ searchTerm: e.target.value })
  }

  handleInputSearchSubmit = (e) => {
    e.preventDefault()
    this.reloadOrdersWithSearchFilter(this.state.searchTerm)
  }

  handleInputSearchClear = () => {
    this.reloadOrdersWithSearchFilter('')
    this.setState({ searchTerm: '' })
  }

  reloadOrdersWithSearchFilter = (searchTerm) => {
    this.props.handlePageLoad(null, null, this.state.rowsPerPage, searchTerm)
    this.setState({ currentPage: 1 })
  }

  render() {
    const { intl, ordersLoading, orders } = this.props
    const { rowsPerPage, currentPage, emptyStateLabel } = this.state
    const currentItemFrom = 1 + rowsPerPage * (currentPage - 1)
    const currentItemTo = rowsPerPage * currentPage

    const pagination = {
      onNextClick: this.handleNextClick,
      onPrevClick: this.handlePrevClick,
      currentItemFrom: currentItemFrom,
      currentItemTo: currentItemTo,
      textShowRows: intl.formatMessage({ id: 'orders.table.showRows' }),
      textOf: intl.formatMessage({ id: 'orders.table.of' }),
      totalItems: this.props.ordersLength,
    }

    return (
      <React.Fragment>
        <Loader loading={ordersLoading} />
        <Table
          fullWidth
          density={'low'}
          schema={this.jsonSchema}
          items={orders}
          emptyStateLabel={emptyStateLabel}
          pagination={pagination}
          onRowClick={({ rowData }) =>
            this.props.handleOrderClick &&
            this.props.handleOrderClick(rowData.orderId)
          }
          toolbar={{
            inputSearch: {
              value: this.state.searchTerm,
              placeholder: intl.formatMessage({ id: 'orders.table.search' }),
              onChange: this.handleInputSearchChange,
              onClear: this.handleInputSearchClear,
              onSubmit: this.handleInputSearchSubmit,
            },
          }}
        />
      </React.Fragment>
    )
  }
}

OrderTable.propTypes = {
  intl: intlShape.isRequired,
  orders: PropTypes.array.isRequired,
  ordersLength: PropTypes.number.isRequired,
  ordersLoading: PropTypes.bool,
  handleOrderClick: PropTypes.func.isRequired,
  handlePageLoad: PropTypes.func.isRequired,
  startCursor: PropTypes.string,
  endCursor: PropTypes.string,
}

export default injectIntl(OrderTable)
